import {
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    Transition,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import {
    ActionType,
    COLLABORATOR_SENTINEL,
    EVERYONE_SENTINEL,
    UserId,
} from "@sp-crm/core";
import { useDebouncedState } from "components/shared/hooks";
import { SelectableUser, UserSelect } from "components/shared/user-select";
import { SearchInput } from "components/ui/search-input";
import { SelectPro } from "components/ui/select-pro";
import * as React from "react";
import { FunctionComponent } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    useCanSeeReferralSourceOwners,
    useCurrentUserId,
    useFeature,
    useIsAllowed,
} from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { ApplicationState } from "store/state";
import { Actions } from "../../../store/actions";
import { CardSortDropdownValues } from "../../../store/reducers/client-search";

export type EnumDictionary<T extends string | number, U> = {
    [key in T]: U;
};

export enum ClientSearchType {
    CoreNames = "bestName",
    AllNames = "anyName",
    Phone = "phone",
}

const placeholderTextVals: EnumDictionary<ClientSearchType, string> = {
    [ClientSearchType.CoreNames]: "Client or best contact name",
    [ClientSearchType.AllNames]: "Client or any contact name",
    [ClientSearchType.Phone]: "Full or partial phone #",
};

interface Props {
    includeCardSort?: boolean;
}

export const ClientSearch: FunctionComponent<Props> = props => {
    const { includeCardSort } = props;
    const dispatch = useDispatch();
    const isReferralFeatureEnabled = useFeature("clientReferralSourceView");
    const isReferralPreferenceEnabled = useCanSeeReferralSourceOwners();
    const showReferralOption = isReferralFeatureEnabled && isReferralPreferenceEnabled;
    const { settings } = useTenantSettings();
    const collaboratorFeatureEnabled =
        useFeature("clientCollaborators") && settings.clientCollaborators;
    const currentUserId = useCurrentUserId();

    const searchState = useSelector(
        (state: ApplicationState) => state.clientSearch.clientSearchInstance,
    );
    const onUpdateSearchText = React.useCallback(
        (searchText: string) => {
            dispatch({
                type: Actions[Actions.CLIENT_SEARCH_TEXT],
                searchText: (searchText ?? "").trim(),
            });
        },
        [dispatch],
    );

    const onUpdateSearchType = React.useCallback(
        (searchType: ClientSearchType) => {
            dispatch({
                type: Actions[Actions.CLIENT_SEARCH_TYPE],
                searchType: searchType,
            });
        },
        [dispatch],
    );

    const onUpdateSearchUser = React.useCallback(
        (userId: SelectableUser) => {
            if (userId === COLLABORATOR_SENTINEL) {
                if (currentUserId) {
                    dispatch({
                        type: Actions[Actions.CLIENTS_LIST_SELECT_COLLABORATOR_USER],
                        userId: currentUserId,
                    });
                }
                return;
            }
            dispatch({
                type: Actions[Actions.CLIENTS_LIST_SELECT_USER],
                userId,
            });
        },
        [dispatch, currentUserId],
    );

    const onUpdateReferralUser = React.useCallback(
        (userId: UserId) => {
            dispatch({
                type: Actions[Actions.CLIENTS_LIST_SELECT_REFERRAL_USER],
                userId,
            });
        },
        [dispatch],
    );

    const onUpdateSort = React.useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            dispatch({
                type: Actions[Actions.DASHBOARD_SORT],
                cardSort: e.target.value,
            });
        },
        [dispatch],
    );

    const [searchText, setSearchText, searchTextDebounced] = useDebouncedState(
        searchState.searchText,
        (value: string) => (value.length < 3 ? 750 : 600),
    );
    const updateSearchText = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value),
        [setSearchText],
    );
    React.useEffect(() => {
        onUpdateSearchText(searchTextDebounced);
    }, [onUpdateSearchText, searchTextDebounced]);

    const canSeeAllOtherClients = useIsAllowed(ActionType.LoadClients);
    const canSeeCollabClients = useIsAllowed(ActionType.LoadCollabClients);
    const canSeeOtherClients = canSeeAllOtherClients || canSeeCollabClients;
    const canSeeReferralSourceOwners = useIsAllowed(ActionType.LoadUsers);

    const isCollaboratorFilterActive =
        collaboratorFeatureEnabled &&
        searchState.collaboratorUserId &&
        searchState.collaboratorUserId !== EVERYONE_SENTINEL;

    const employeeFilterValue = isCollaboratorFilterActive
        ? COLLABORATOR_SENTINEL
        : searchState.selectedUserId;

    return (
        <>
            <div className="hidden lg:flex lg:flex-wrap lg:justify-between gap-x-4 gap-y-2 items-end action-bar">
                <div className="flex flex-wrap gap-2">
                    <div>
                        <SearchInput
                            className="w-64"
                            type="text"
                            onChange={updateSearchText}
                            value={searchText}
                            placeholder={placeholderTextVals[searchState.searchType]}
                        />
                    </div>
                    <div>
                        <SelectPro
                            onChange={e =>
                                onUpdateSearchType(e.target.value as ClientSearchType)
                            }
                            includePlaceholderOption={false}
                            value={searchState.searchType}
                            options={[
                                {
                                    value: ClientSearchType.CoreNames,
                                    text: "Search client or best contact name",
                                },
                                {
                                    value: ClientSearchType.AllNames,
                                    text: "Search client or any contact name",
                                },
                                {
                                    value: ClientSearchType.Phone,
                                    text: "Search by any contact phone",
                                },
                            ]}
                        />
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    {includeCardSort ? (
                        <div>
                            <SelectPro
                                label="Sort by"
                                includePlaceholderOption={false}
                                className="dashboard-sortby"
                                value={searchState.cardSortPreference}
                                options={CardSortDropdownValues}
                                onChange={onUpdateSort}
                            />
                        </div>
                    ) : null}
                    {showReferralOption ? (
                        <div>
                            <UserSelect
                                label="Filter by referral assignee"
                                onChange={onUpdateReferralUser}
                                value={searchState.referralSourceUserId}
                                includeUnassigned
                                includeEveryone
                                disabled={!canSeeReferralSourceOwners}
                            />
                        </div>
                    ) : null}
                    <div>
                        <UserSelect
                            label="Filter by Employee"
                            onChange={onUpdateSearchUser}
                            value={employeeFilterValue}
                            includeUnassigned
                            includeEveryone
                            disabled={!canSeeOtherClients}
                            includeCollaboratorOption={collaboratorFeatureEnabled}
                        />
                    </div>
                </div>
            </div>

            <div className="lg:hidden action-bar">
                <Disclosure defaultOpen={false}>
                    {({ open }) => (
                        <>
                            <div className="flex items-center justify-between mb-2">
                                <SearchInput
                                    className="flex-1 mr-2"
                                    type="text"
                                    onChange={updateSearchText}
                                    value={searchText}
                                    placeholder={
                                        placeholderTextVals[searchState.searchType]
                                    }
                                />
                                <DisclosureButton className="flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                    <span className="mr-1">Filters</span>
                                    <ChevronDownIcon
                                        className={`${
                                            open ? "rotate-180 transform" : ""
                                        } h-4 w-4 transition-transform duration-200`}
                                    />
                                </DisclosureButton>
                            </div>
                            <Transition
                                enter="transition duration-200 ease-out"
                                enterFrom="transform -translate-y-2 opacity-0"
                                enterTo="transform translate-y-0 opacity-100"
                                leave="transition duration-150 ease-out"
                                leaveFrom="transform translate-y-0 opacity-100"
                                leaveTo="transform -translate-y-2 opacity-0">
                                <DisclosurePanel className="space-y-2 pb-2">
                                    <div>
                                        <SelectPro
                                            onChange={e =>
                                                onUpdateSearchType(
                                                    e.target.value as ClientSearchType,
                                                )
                                            }
                                            includePlaceholderOption={false}
                                            value={searchState.searchType}
                                            options={[
                                                {
                                                    value: ClientSearchType.CoreNames,
                                                    text: "Search client or best contact name",
                                                },
                                                {
                                                    value: ClientSearchType.AllNames,
                                                    text: "Search client or any contact name",
                                                },
                                                {
                                                    value: ClientSearchType.Phone,
                                                    text: "Search by any contact phone",
                                                },
                                            ]}
                                        />
                                    </div>
                                    {includeCardSort ? (
                                        <div>
                                            <SelectPro
                                                label="Sort by"
                                                includePlaceholderOption={false}
                                                className="dashboard-sortby"
                                                value={searchState.cardSortPreference}
                                                options={CardSortDropdownValues}
                                                onChange={onUpdateSort}
                                            />
                                        </div>
                                    ) : null}
                                    {showReferralOption ? (
                                        <div>
                                            <UserSelect
                                                label="Filter by referral assignee"
                                                onChange={onUpdateReferralUser}
                                                value={searchState.referralSourceUserId}
                                                includeUnassigned
                                                includeEveryone
                                                disabled={!canSeeReferralSourceOwners}
                                            />
                                        </div>
                                    ) : null}
                                    <div>
                                        <UserSelect
                                            label="Filter by Employee"
                                            onChange={onUpdateSearchUser}
                                            value={employeeFilterValue}
                                            includeUnassigned
                                            includeEveryone
                                            disabled={!canSeeOtherClients}
                                            includeCollaboratorOption={
                                                collaboratorFeatureEnabled
                                            }
                                        />
                                    </div>
                                </DisclosurePanel>
                            </Transition>
                        </>
                    )}
                </Disclosure>
            </div>
        </>
    );
};
